import React from 'react';
import PropTypes from 'prop-types';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import useUrgencyDiscountUIEnabled from 'hooks/useUrgencyDiscountUIEnabled';
import { hasUrgencyDiscountAmount, isResultDiscountUIType } from 'utils/urgencyDiscount';
import OpenTicketWrapper from './OpenTicketWrapper';
import OpenTicketResult from './OpenTicketResult';
import OpenTicketButton from './OpenTicketButton';
import TerminalGroupedOpenTicket from './TerminalGroupedOpenTicket';

/**
 * Renders the OpenTicketCTA component.
 * @param {Object} props - The component props.
 * @param {Object} props.openTrip - The open trip object.
 * @param {string} props.way - The way of the trip.
 * @param {boolean} props.isRoundTrip - Indicates if it is a round trip.
 * @param {function} props.onSelect - The function to handle trip selection.
 * @param {boolean} props.isTerminalGrouped - Indicates if the CTA belongs to a terminal group.
 * @param {boolean} props.showWalletPoints - Indicates if the wallet price should be shown.
 * @param {string} props.walletType - The wallet type.
 * @param {boolean} props.shouldShowProviderDiscount - Whether visible passenger pricing supports the provider discount.
 * @returns {JSX.Element|null} The rendered OpenTicketCTA component.
 */
const OpenTicketCTA = ({
  openTrip,
  way,
  isRoundTrip,
  onSelect,
  isTerminalGrouped,
  showWalletPoints,
  walletType,
  shouldShowProviderDiscount,
}) => {
  const features = useWhitelabelFeatures();
  const urgencyDiscountUIEnabled = useUrgencyDiscountUIEnabled();
  const isNewResultsDesign = features.NEW_RESULTS_DESIGN;

  if (!openTrip) return null;

  const roundPricingKey = way === 'departure' ? 'departureRoundTripPricing' : 'roundTripPricing';
  const pricingKey = isRoundTrip ? roundPricingKey : 'pricing';

  const pricing = openTrip[pricingKey];
  const { total, providerDiscount: tripDiscount, walletPoints } = pricing;
  const hasProviderDiscount = hasUrgencyDiscountAmount(tripDiscount);
  const showDiscountUrgency =
    shouldShowProviderDiscount &&
    urgencyDiscountUIEnabled &&
    hasProviderDiscount &&
    isResultDiscountUIType(pricing.discountType);
  const showClassicDiscount =
    shouldShowProviderDiscount && hasProviderDiscount && !showDiscountUrgency;

  const useWalletPoints = features.CAN_USE_POINTS_OPEN_TICKET && showWalletPoints;

  if (!isNewResultsDesign && features.SHOW_OPEN_TICKET_ON_RESULTS) {
    return (
      <OpenTicketResult
        trip={openTrip}
        providerDiscount={tripDiscount}
        total={total}
        selectTrip={onSelect}
        provider={openTrip.line.logoUrl}
        providerName={openTrip.line.name}
        way="departure"
        windowTicket={tripDiscount ? total + tripDiscount.amount : total}
        walletPoints={useWalletPoints ? walletPoints : 0}
      />
    );
  }
  if (isNewResultsDesign || features.SHOW_OPEN_TICKET_BUTTON) {
    if (isTerminalGrouped) {
      return (
        <div className="fade-in mt-1 w-full">
          <TerminalGroupedOpenTicket
            selectTrip={onSelect}
            total={total}
            trip={openTrip}
            walletPoints={walletPoints}
            showWalletPoints={useWalletPoints}
            walletType={walletType}
            providerDiscount={pricing.providerDiscount}
            pricing={pricing}
            showDiscountUrgency={showDiscountUrgency}
            showClassicDiscount={showClassicDiscount}
            way={way}
          />
        </div>
      );
    }

    return (
      <OpenTicketWrapper>
        <OpenTicketButton
          trip={openTrip}
          total={total}
          selectTrip={onSelect}
          walletPoints={walletPoints}
          showWalletPoints={useWalletPoints}
          walletType={walletType}
        />
      </OpenTicketWrapper>
    );
  }
  return null;
};

OpenTicketCTA.propTypes = {
  openTrip: PropTypes.object.isRequired,
  way: PropTypes.string.isRequired,
  isRoundTrip: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  isTerminalGrouped: PropTypes.bool,
  showWalletPoints: PropTypes.bool,
  walletType: PropTypes.string,
  shouldShowProviderDiscount: PropTypes.bool,
};

OpenTicketCTA.defaultProps = {
  isTerminalGrouped: false,
  showWalletPoints: false,
  walletType: undefined,
  shouldShowProviderDiscount: true,
};

export default OpenTicketCTA;
